import { LitElement, html, svg, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { registerChartFrame } from './chart-clock';
import { ChartHoverTooltip } from './hover-tooltip';
import type { RingBuffer } from './ring-buffer';
import {
  CHART_THEMES,
  calculateYAxisScale,
  calculateXAxisTicks,
  generateChartPaths,
  generateGhostPath,
  computeWindowStats,
  formatStatValue,
  formatStatLabel,
  formatTimeDelta,
  formatChartValue,
  formatChartTime,
  getGhostColor,
  getThresholdColor,
  interpolateAt,
  nearestIndex,
  rangeStats,
  snapValue,
  type ChartConfig,
  type ChartGhost,
  type ChartStats,
  type ChartThreshold,
  type MeasurePair,
  type YAxisScale,
  type ChartSeries,
} from './chart-types';

/*
 * <vitesse-chart> — the full time-series chart: the v2 UniversalChart on the Lit
 * substrate, drawn the same way. Lit renders the STATIC structure (gridlines,
 * axes, tick marks, labels, gradient def, thresholds, measure cursors) and
 * re-renders only when the scale, the size or the analytical state changes;
 * the HIGH-FREQUENCY line/fill/current-value/crosshair attributes are mutated
 * imperatively on the shared chart clock — never through Lit reactivity.
 *
 * Data flows in via a RingBuffer SHARED BY REFERENCE with the owner; this
 * element owns no samples. Structural colours are CSS tokens (light/dark free);
 * the line/area use the shared CHART_THEMES data palette.
 *
 * ANALYTICS ARE THE OWNER'S. Thresholds, the measure tool and ghosts are drawn
 * here from properties and every change is reported as an event; the owner
 * (through ChartAnalytics) decides what persists. The chart never writes config.
 */

/** Below this width the axis furniture would be most of the picture: tighter margins, end labels only. */
const COMPACT_W = 280;
/** Below this width even two end labels collide; the axis lines alone say where the plot is. */
const LABELS_W = 170;
/** A tick label's width, per character, at the axis font size — the left margin is earned by the labels. */
const CHAR_W = 6.2;
const FONT = { axis: 10, value: 11 };
/** How far (as a share of the window) the cursor may sit from the nearest sample and still read a value. */
const HOVER_REACH = 0.1;
let chartUid = 0;

export type ThresholdCrossDirection = 'entered' | 'exited';

@customElement('vitesse-chart')
export class VitesseChart extends LitElement {
  @property({ attribute: false }) ring?: RingBuffer;
  @property({ attribute: false }) config!: ChartConfig;
  /** pausedTimestamp — freezes the window + disables live scroll when set. */
  @property({ type: Number }) paused: number | null = null;
  @property({ attribute: false }) ghosts: ChartGhost[] = [];
  /** Further traces beside the primary ring, each on its own scale; the first one owns the right axis. */
  @property({ attribute: false }) series: ChartSeries[] = [];
  /** One scale per extra series, drawn as its own column on the right. */
  @state() private rightScales: YAxisScale[] = [];
  private rightSigs: string[] = [];
  /** Running min/max the owner tracks — the axis anchor. */
  @property({ attribute: false }) allTimeStats: ChartStats | null = null;
  @property({ type: Boolean }) showCurrentValueLabel = true;
  @property({ attribute: false }) thresholds: ChartThreshold[] = [];
  @property({ type: Boolean }) thresholdMode = false;
  @property({ type: Boolean }) cursorPairMode = false;
  @property({ attribute: false }) measurePairs: MeasurePair[] = [];

  @state() private w = 0;
  @state() private h = 0;
  @state() private preview: { y: number; value: number } | null = null;
  @state() private draggingCursor: { pairId: string; which: 'a' | 'b' } | null = null;

  private readonly uid = ++chartUid;
  private readonly gradientId = `vc-grad-${this.uid}`;
  private readonly clipId = `vc-clip-${this.uid}`;

  private unClock?: () => void;
  private ro?: ResizeObserver;
  private scale: YAxisScale = { minValue: 0, maxValue: 100, ticks: [] };
  private scaleSig = '';
  private pausedDrawn = false;
  private measureTick = 0;

  // Cached SVG nodes mutated every frame (re-queried after each Lit render).
  private elLine: SVGPathElement | null = null;
  private elFill: SVGPathElement | null = null;
  private elDot: SVGCircleElement | null = null;
  private elValText: SVGTextElement | null = null;
  private elCrossV: SVGLineElement | null = null;
  private elCrossH: SVGLineElement | null = null;
  private elCrossDot: SVGCircleElement | null = null;
  private statEls: Record<string, HTMLElement | null> = {};

  /** Hover state: relX (0-1 across the plot) + the mouse in viewport px. */
  private hover: { relX: number; clientX: number; clientY: number } | null = null;
  /** relX of the earliest sample in view — left of it there is no data to read. */
  private dataLeftBound = 0;
  private tooltip = new ChartHoverTooltip();
  private draggingThreshold: string | null = null;
  private thresholdDragMoved = false;
  private crossState = new Map<string, boolean>();

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.ro = new ResizeObserver(() => this.measure());
    this.ro.observe(this);
    this.measure();
    this.unClock = registerChartFrame((now) => this.frame(now));
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
    this.unClock?.();
    this.tooltip.dispose();
    this.elLine = this.elFill = this.elDot = this.elValText = null;
    this.elCrossV = this.elCrossH = this.elCrossDot = null;
  }

  private measure(): void {
    const r = this.getBoundingClientRect();
    const w = Math.floor(r.width);
    const h = Math.floor(r.height);
    if (w !== this.w || h !== this.h) {
      this.w = w;
      this.h = h;
    }
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('paused')) this.pausedDrawn = false;
    if (changed.has('thresholdMode') && !this.thresholdMode) this.preview = null;
    if (changed.has('cursorPairMode') && !this.cursorPairMode) this.draggingCursor = null;
  }

  protected updated(_changed: PropertyValues): void {
    // The DATA line color flows via a custom property (SCSS paints the SVG from
    // it) — so the per-frame display toggles never fight a Lit style binding.
    this.style.setProperty('--vc-line', this.lineColor);
    this.elLine = this.querySelector('.vc-line');
    this.elFill = this.querySelector('.vc-fill');
    this.elDot = this.querySelector('.vc-dot');
    this.elValText = this.querySelector('.vc-val');
    this.elCrossV = this.querySelector('.vc-cross-v');
    this.elCrossH = this.querySelector('.vc-cross-h');
    this.elCrossDot = this.querySelector('.vc-cross-dot');
    this.statEls = {
      min: this.querySelector('[data-stat="min"]'),
      max: this.querySelector('[data-stat="max"]'),
      avg: this.querySelector('[data-stat="avg"]'),
      rms: this.querySelector('[data-stat="rms"]'),
    };
    // A re-render replaced the nodes; draw into them at once rather than on the next tick.
    this.pausedDrawn = false;
    this.frame(this.paused ?? Date.now());
  }

  private get lineColor(): string {
    return (CHART_THEMES[this.config?.theme ?? 'frost'] ?? CHART_THEMES.frost).line;
  }
  private get compact(): boolean {
    return this.w > 0 && this.w < COMPACT_W;
  }
  /** Margins sized by what sits in them: the widest y label on the left, the value label on the
   *  right (none when compact), a line of x labels below (none when too narrow for even two). */
  private get margin(): { top: number; right: number; bottom: number; left: number } {
    const compact = this.compact;
    const labels = this.scale.ticks.length ? this.scale.ticks : [{ label: '0.000' } as { label: string }];
    const widest = Math.max(...labels.map((t) => t.label.length));
    const left = Math.round(widest * CHAR_W) + (compact ? 6 : 10);
    // Every extra series owns a right axis column; each needs the room its labels take.
    const rightW = compact ? 0 : this.rightWidths.reduce((a, w) => a + w, 0);
    return {
      top: compact ? 8 : 12,
      right: compact ? 8 : Math.max(this.showCurrentValueLabel ? 34 : 8, rightW),
      bottom: this.w < LABELS_W ? 6 : compact ? 14 : 16,
      left,
    };
  }
  /** Column width per right axis: its widest label plus the tick and a gutter. */
  private get rightWidths(): number[] {
    return this.rightScales.map((sc) => (sc.ticks.length ? Math.round(Math.max(...sc.ticks.map((t) => t.label.length)) * CHAR_W) + 12 : 0));
  }
  private get chartW(): number {
    return Math.max(0, this.w - this.margin.left - this.margin.right);
  }
  private get chartH(): number {
    return Math.max(0, this.h - this.margin.top - this.margin.bottom);
  }
  private valueToY(value: number): number {
    const range = this.scale.maxValue - this.scale.minValue;
    return range > 0 ? this.chartH - ((value - this.scale.minValue) / range) * this.chartH : this.chartH / 2;
  }
  private yToValue(y: number): number {
    const range = this.scale.maxValue - this.scale.minValue;
    return this.scale.maxValue - (y / this.chartH) * range;
  }
  private now(): number {
    return this.paused ?? Date.now();
  }

  // ── per-frame redraw (imperative; no Lit render) ───────────────────────────
  private frame(frameNow: number): void {
    const ring = this.ring;
    // A Lit update requested before removal still runs after it; drawing then
    // would re-create the body-appended tooltip with nobody left to remove it.
    if (!this.isConnected || !ring || this.chartW <= 0 || this.chartH <= 0) return;
    // Paused: one draw with the frozen instant, then nothing until something changes.
    if (this.paused != null) {
      if (this.pausedDrawn) return;
      this.pausedDrawn = true;
    }
    const now = this.paused ?? frameNow;

    // Scale: recompute every frame (cheap), but only re-render the static
    // gridlines/labels when it actually changes (allTimeStats keeps it stable).
    this.scale = calculateYAxisScale(ring, this.chartH, this.config, now, this.allTimeStats);
    const sig = `${this.scale.minValue}|${this.scale.maxValue}|${this.scale.ticks.length}`;
    if (sig !== this.scaleSig) {
      this.scaleSig = sig;
      this.requestUpdate();
    }

    // Where the data starts in the viewport — everything left of it is empty.
    if (ring.count > 0) {
      const earliest = ring.timestamps[ring.head];
      const rel = (earliest - (now - this.config.timeWindowMs)) / this.config.timeWindowMs;
      this.dataLeftBound = Math.max(0, Math.min(1, rel));
    } else {
      this.dataLeftBound = 1;
    }

    const { linePath, fillPath, lastPointY } = generateChartPaths(
      ring, this.chartW, this.chartH, this.config, this.scale.minValue, this.scale.maxValue, now,
    );
    this.elLine?.setAttribute('d', linePath);
    this.elFill?.setAttribute('d', fillPath);

    // The other series, each fitted to its own window range: a pinned axis
    // belongs to the primary trace alone.
    if (this.series.length) {
      const free = { ...this.config, yAxisMin: undefined, yAxisMax: undefined };
      const lines = this.querySelectorAll<SVGPathElement>('.vc-line-extra');
      this.series.forEach((sr, i) => {
        const sc = calculateYAxisScale(sr.ring, this.chartH, free, now, null);
        const rs = `${sc.minValue}|${sc.maxValue}|${sc.ticks.length}`;
        if (rs !== this.rightSigs[i]) {
          const sigs = this.rightSigs.slice(), scales = this.rightScales.slice();
          sigs[i] = rs; scales[i] = sc;
          this.rightSigs = sigs; this.rightScales = scales;
        }
        const p = generateChartPaths(sr.ring, this.chartW, this.chartH, free, sc.minValue, sc.maxValue, now);
        lines[i]?.setAttribute('d', p.linePath);
      });
    }

    // Current value dot + label, pinned at the right edge.
    const lastVal = ring.last();
    const showDot = lastPointY !== null && lastVal !== null;
    if (this.elDot) {
      if (showDot) {
        this.elDot.setAttribute('cy', String(lastPointY));
        this.elDot.style.display = '';
      } else this.elDot.style.display = 'none';
    }
    if (this.elValText) {
      if (showDot && !this.compact) {
        this.elValText.setAttribute('y', String(lastPointY));
        const v = lastVal as number;
        this.elValText.textContent = v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v >= 1 ? v.toFixed(2) : v.toFixed(4);
        this.elValText.style.display = '';
      } else this.elValText.style.display = 'none';
    }

    this.updateCrosshair(now);
    this.updateStats(ring, now);
    this.checkThresholdCrossings(ring);

    // The measure panels read the data under their cursors, which scrolls; refresh them a few times a second.
    if (this.cursorPairMode && this.measurePairs.some((p) => p.relXB !== null) && ++this.measureTick % 15 === 0) this.requestUpdate();
  }

  /** The stats overlay's figures, written straight into their spans (they change every sample). */
  private updateStats(ring: RingBuffer, now: number): void {
    if (!this.config.showStats || !this.statEls.min) return;
    const s = computeWindowStats(ring, this.config.timeWindowMs, now);
    if (!s) return;
    this.statEls.min!.textContent = formatStatValue(s.min);
    this.statEls.max!.textContent = formatStatValue(s.max);
    this.statEls.avg!.textContent = formatStatValue(s.avg);
    this.statEls.rms!.textContent = formatStatValue(s.rms);
  }

  /** Pause-on-cross: report each threshold the newest value just crossed. */
  private checkThresholdCrossings(ring: RingBuffer): void {
    if (!this.thresholds.length || ring.count === 0) return;
    const latest = ring.last() as number;
    for (const t of this.thresholds) {
      if (!t.pauseOnCross) continue;
      const prev = this.crossState.get(t.id);
      const isAbove = latest >= t.value;
      if (prev !== undefined && prev !== isAbove) {
        const direction: ThresholdCrossDirection = (t.condition === 'above' && isAbove) || (t.condition === 'below' && !isAbove) ? 'entered' : 'exited';
        this.dispatchEvent(new CustomEvent('threshold-cross', { detail: { threshold: t, direction }, bubbles: true, composed: true }));
      }
      this.crossState.set(t.id, isAbove);
    }
  }

  private hideCrosshair(): void {
    if (this.elCrossV) this.elCrossV.style.display = 'none';
    if (this.elCrossH) this.elCrossH.style.display = 'none';
    if (this.elCrossDot) this.elCrossDot.style.display = 'none';
    this.tooltip.hide();
  }

  private updateCrosshair(now: number): void {
    const ring = this.ring;
    const hover = this.hover;
    if (!hover || !ring || ring.count === 0 || hover.relX < this.dataLeftBound) {
      this.hideCrosshair();
      return;
    }
    const timeAtCursor = now - this.config.timeWindowMs * (1 - hover.relX);
    const at = interpolateAt(ring, timeAtCursor);
    if (!at) {
      this.hideCrosshair();
      return;
    }
    // Too far from any sample to claim a reading (past the newest is fine — it is held).
    const lastTs = ring.timestamps[(ring.head + ring.count - 1) % ring.capacity];
    if (timeAtCursor < lastTs) {
      const i = nearestIndex(ring, timeAtCursor);
      const dist = Math.abs(ring.timestamps[(ring.head + i) % ring.capacity] - timeAtCursor);
      if (dist > this.config.timeWindowMs * HOVER_REACH) {
        this.hideCrosshair();
        return;
      }
    }
    const x = hover.relX * this.chartW;
    const y = this.valueToY(at.value);
    if (this.elCrossV) {
      this.elCrossV.setAttribute('x1', String(x));
      this.elCrossV.setAttribute('x2', String(x));
      this.elCrossV.style.display = '';
    }
    if (this.elCrossH) {
      this.elCrossH.setAttribute('y1', String(y));
      this.elCrossH.setAttribute('y2', String(y));
      this.elCrossH.style.display = '';
    }
    if (this.elCrossDot) {
      this.elCrossDot.setAttribute('cx', String(x));
      this.elCrossDot.setAttribute('cy', String(y));
      this.elCrossDot.style.display = '';
    }
    // The chip follows the mouse and stays on screen: flipped left of the cursor
    // near the right edge, below it near the top, above it near the bottom.
    const est = 180;
    const tipH = 28;
    const pad = 10;
    let tx = hover.clientX + 12;
    let ty = hover.clientY - 30;
    if (tx + est > window.innerWidth - pad) tx = hover.clientX - est - 12;
    if (ty < pad) ty = hover.clientY + 10;
    if (ty + tipH > window.innerHeight - pad) ty = hover.clientY - tipH - 10;
    this.tooltip.show({
      cursorClientX: tx - 12,
      cursorClientY: ty + 30,
      timeText: formatChartTime(timeAtCursor),
      // Every trace at this instant: the primary, then each extra series read off its own ring.
      rows: [{ color: this.lineColor, valueText: formatChartValue(at.value) }].concat(
        this.series.map((sr) => { const v = interpolateAt(sr.ring, timeAtCursor); return { color: sr.color, valueText: (v ? formatChartValue(v.value) : '--') + (sr.unit ? ' ' + sr.unit : '') }; }),
      ),
    });
  }

  // ── pointer handling ───────────────────────────────────────────────────────
  private plotPoint(e: MouseEvent): { x: number; y: number } {
    const rect = this.getBoundingClientRect();
    return { x: e.clientX - rect.left - this.margin.left, y: e.clientY - rect.top - this.margin.top };
  }

  private readonly onMove = (e: MouseEvent): void => {
    const { x, y } = this.plotPoint(e);

    if (this.draggingThreshold) {
      this.thresholdDragMoved = true;
      const clampedY = Math.max(0, Math.min(this.chartH, y));
      const value = snapValue(this.yToValue(clampedY), this.scale.minValue, this.scale.maxValue);
      this.dispatchEvent(new CustomEvent('threshold-drag', { detail: { id: this.draggingThreshold, value }, bubbles: true, composed: true }));
      return;
    }
    if (this.draggingCursor) {
      const clampedX = Math.max(0, Math.min(this.chartW, x));
      const relX = Math.max(this.dataLeftBound, clampedX / this.chartW);
      const d = this.draggingCursor;
      const pairs = this.measurePairs.map((p) => (p.id !== d.pairId ? p : d.which === 'a' ? { ...p, relXA: relX } : { ...p, relXB: relX }));
      this.dispatchEvent(new CustomEvent('measure-change', { detail: pairs, bubbles: true, composed: true }));
      return;
    }
    if (x < 0 || x > this.chartW || y < 0 || y > this.chartH) {
      this.hover = null;
      this.hideCrosshair();
      if (this.preview) this.preview = null;
      return;
    }
    if (this.thresholdMode) {
      this.preview = { y, value: this.yToValue(y) };
      this.hover = null;
      this.hideCrosshair();
      return;
    }
    if (!this.ring || this.ring.count === 0) {
      this.hover = null;
      this.hideCrosshair();
      return;
    }
    this.hover = { relX: x / this.chartW, clientX: e.clientX, clientY: e.clientY };
    this.updateCrosshair(this.now());
  };

  private readonly onLeave = (): void => {
    this.hover = null;
    this.hideCrosshair();
    if (this.preview) this.preview = null;
  };

  private readonly onClick = (e: MouseEvent): void => {
    const { x, y } = this.plotPoint(e);
    if (x < 0 || x > this.chartW || y < 0 || y > this.chartH) return;
    if (this.thresholdMode) {
      const value = snapValue(this.yToValue(y), this.scale.minValue, this.scale.maxValue);
      this.dispatchEvent(new CustomEvent('threshold-place', { detail: { value }, bubbles: true, composed: true }));
      return;
    }
    if (this.cursorPairMode) {
      const relX = Math.max(this.dataLeftBound, x / this.chartW);
      this.dispatchEvent(new CustomEvent('measure-place', { detail: { relX, additive: e.shiftKey }, bubbles: true, composed: true }));
    }
  };

  private readonly onUp = (): void => {
    if (this.draggingThreshold) this.dispatchEvent(new CustomEvent('threshold-drag-end', { bubbles: true, composed: true }));
    this.draggingThreshold = null;
    if (this.draggingCursor) this.draggingCursor = null;
  };

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.draggingThreshold) this.draggingThreshold = null;
  };

  // ── measure statistics (per render) ────────────────────────────────────────
  private measureStats(now: number) {
    const ring = this.ring;
    if (!ring || ring.count === 0) return [];
    const out: { pair: MeasurePair; xA: number; xB: number; a: number; b: number; deltaT: number; deltaV: number; slope: number; min: number; max: number }[] = [];
    for (const p of this.measurePairs) {
      if (p.relXB === null) continue;
      const tA = now - this.config.timeWindowMs * (1 - p.relXA);
      const tB = now - this.config.timeWindowMs * (1 - p.relXB);
      const a = interpolateAt(ring, tA);
      const b = interpolateAt(ring, tB);
      if (!a || !b) continue;
      const deltaT = Math.abs(b.timestamp - a.timestamp);
      const deltaV = b.value - a.value;
      const slope = deltaT > 0 ? deltaV / (deltaT / 1000) : 0;
      const { min, max } = rangeStats(ring, Math.min(a.timestamp, b.timestamp), Math.max(a.timestamp, b.timestamp), a.value);
      out.push({ pair: p, xA: p.relXA * this.chartW, xB: p.relXB * this.chartW, a: a.value, b: b.value, deltaT, deltaV, slope, min, max });
    }
    return out;
  }

  render() {
    const w = this.w;
    const h = this.h;
    if (w <= 0 || h <= 0) return html`<div class="vc-empty"></div>`;
    const cw = this.chartW;
    const ch = this.chartH;
    const theme = CHART_THEMES[this.config?.theme ?? 'frost'] ?? CHART_THEMES.frost;
    const lineColor = theme.line;
    const allX = calculateXAxisTicks(this.config.timeWindowMs, cw);
    const compact = this.compact;
    // Every tick still draws its gridline; only the end labels fit a compact plot.
    const xticks = allX;
    const xlabels = this.w < LABELS_W ? [] : compact ? allX.filter((_tk, i) => i === 0 || i === allX.length - 1) : allX;
    const valueLabel = this.showCurrentValueLabel && !compact && this.series.length === 0;
    const M = this.margin;
    const ghostPaths = (this.ghosts ?? []).map((g, i) => ({
      d: generateGhostPath(g, cw, ch, this.config.timeWindowMs, this.scale.minValue, this.scale.maxValue),
      color: getGhostColor(i),
    }));
    const visibleThresholds = this.thresholds
      .map((t, i) => ({ t, i, y: this.valueToY(t.value), color: getThresholdColor(i) }))
      .filter((x) => x.y >= 0 && x.y <= ch);
    const measures = this.cursorPairMode ? this.measureStats(this.now()) : [];

    return html`
      <svg class="vc-svg" width=${w} height=${h} @mousemove=${this.onMove} @mouseleave=${this.onLeave} @click=${this.onClick} @mouseup=${this.onUp}>
        <defs>
          <linearGradient id=${this.gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color=${theme.gradient[0]} stop-opacity="0.2"></stop>
            <stop offset="100%" stop-color=${theme.gradient[1]} stop-opacity="0.05"></stop>
          </linearGradient>
          <clipPath id=${this.clipId}><rect x="0" y="0" width=${cw} height=${ch}></rect></clipPath>
        </defs>
        <g transform="translate(${M.left}, ${M.top})">
          ${this.scale.ticks.map((tk) => svg`<line class="vc-grid" x1="0" y1=${tk.y} x2=${cw} y2=${tk.y}></line>`)}
          ${xticks.map((tk) => svg`<line class="vc-grid" x1=${tk.x} y1="0" x2=${tk.x} y2=${ch}></line>`)}

          <g clip-path="url(#${this.clipId})">
            ${ghostPaths.map((g) => (g.d ? svg`<path class="vc-ghost chart-ghost-path" d=${g.d} style=${`stroke:${g.color}`}></path>` : nothing))}
            <path class="vc-fill" fill="url(#${this.gradientId})"></path>
            <path class="vc-line"></path>
            ${this.series.map((sr) => svg`<path class="vc-line vc-line-extra" style=${`stroke:${sr.color}`}></path>`)}

            ${visibleThresholds.map(
              ({ t, y, color }) => svg`<g>
                <line class="vc-threshold" x1="0" y1=${y} x2=${cw} y2=${y} style=${`stroke:${color}`}></line>
                <text class="chart-threshold-label" x="8" y=${y - 4} text-anchor="start" fill=${color} fill-opacity="0.9">${formatStatLabel(t.value)}</text>
              </g>`,
            )}
            ${this.thresholdMode && this.preview
              ? svg`<g>
                  <line class="vc-threshold-preview" x1="0" y1=${this.preview.y} x2=${cw} y2=${this.preview.y} style=${`stroke:${lineColor}`}></line>
                  <text class="chart-threshold-label" x=${cw - 4} y=${this.preview.y - 4} text-anchor="end" fill=${lineColor} fill-opacity="0.6">${formatStatLabel(this.preview.value)}</text>
                </g>`
              : nothing}

            ${this.cursorPairMode
              ? this.measurePairs.map((p) => {
                  const xA = p.relXA * cw;
                  const xB = p.relXB !== null ? p.relXB * cw : null;
                  const st = measures.find((m) => m.pair.id === p.id);
                  return svg`<g>
                    <line class="vc-measure-line" x1=${xA} y1="0" x2=${xA} y2=${ch}></line>
                    <rect class="vc-measure-grip" x=${xA - 4} y="0" width="8" height=${ch}
                      @mousedown=${(e: MouseEvent) => { e.stopPropagation(); this.draggingCursor = { pairId: p.id, which: 'a' }; }}></rect>
                    ${xB !== null
                      ? svg`<line class="vc-measure-line" x1=${xB} y1="0" x2=${xB} y2=${ch}></line>
                        <rect class="vc-measure-grip" x=${xB - 4} y="0" width="8" height=${ch}
                          @mousedown=${(e: MouseEvent) => { e.stopPropagation(); this.draggingCursor = { pairId: p.id, which: 'b' }; }}></rect>
                        <rect class="vc-measure-shade" x=${Math.min(xA, xB)} y="0" width=${Math.abs(xB - xA)} height=${ch}></rect>`
                      : nothing}
                    ${st ? svg`<line class="vc-measure-fit" x1=${st.xA} y1=${this.valueToY(st.a)} x2=${st.xB} y2=${this.valueToY(st.b)}></line>` : nothing}
                  </g>`;
                })
              : nothing}

            <line class="vc-cross vc-cross-v" x1="0" y1="0" x2="0" y2=${ch} style="display:none"></line>
            <line class="vc-cross vc-cross-h" x1="0" y1="0" x2=${cw} y2="0" style="display:none"></line>
            <circle class="vc-cross-dot" r="4" style="display:none"></circle>
          </g>

          <circle class="vc-dot" cx=${cw} r="3" style="display:none"></circle>

          <line class="vc-axis" x1="0" y1=${ch} x2=${cw} y2=${ch}></line>
          <line class="vc-axis" x1="0" y1="0" x2="0" y2=${ch}></line>

          ${this.scale.ticks.map(
            (tk, i, all) => svg`<g>
              <line class="vc-axis" x1="-4" y1=${tk.y} x2="0" y2=${tk.y} style=${this.series.length ? `stroke:${this.lineColor}` : nothing}></line>
              ${!compact || i === 0 || i === all.length - 1
                ? svg`<text class="vc-axis-label vc-y" x="-6" y=${tk.y} dy="0.32em" text-anchor="end" font-size=${FONT.axis}>${tk.label}</text>`
                : nothing}
            </g>`,
          )}
          ${this.series.length && !compact
            ? this.rightScales.map((sc, i) => {
                // Each extra series gets its own column; the tick mark carries the colour, the label stays neutral.
                const x = cw + this.rightWidths.slice(0, i).reduce((a, w) => a + w, 0);
                return sc.ticks.map(
                  (tk) => svg`<g>
                    <line class="vc-axis" x1=${x} y1=${tk.y} x2=${x + 4} y2=${tk.y} style=${`stroke:${this.series[i]?.color}`}></line>
                    <text class="vc-axis-label vc-y vc-y-right" x=${x + 6} y=${tk.y} dy="0.32em" text-anchor="start" font-size=${FONT.axis}>${tk.label}</text>
                  </g>`,
                );
              })
            : nothing}
          ${xticks.map((tk) => svg`<line class="vc-axis" x1=${tk.x} y1=${ch} x2=${tk.x} y2=${ch + 4}></line>`)}
          ${xlabels.map(
            (tk, i) => svg`<text class="vc-axis-label vc-x" x=${tk.x} y=${ch + 14}
              text-anchor=${compact ? (i === 0 ? 'start' : 'end') : 'middle'} font-size=${FONT.axis}>${tk.label}</text>`,
          )}

          ${valueLabel ? svg`<text class="vc-val" x=${cw + 8} dy="0.32em" font-size=${FONT.value} style="display:none"></text>` : nothing}

          <rect class="vc-hit" x="0" y="0" width=${cw} height=${ch}></rect>

          ${visibleThresholds.map(
            ({ t, y, color }) => svg`<g>
              <line class="chart-threshold-line-hit" x1="0" y1=${y} x2=${cw} y2=${y}
                @click=${(e: MouseEvent) => { e.stopPropagation(); this.dispatchEvent(new CustomEvent('threshold-click', { detail: { id: t.id }, bubbles: true, composed: true })); }}></line>
              <circle class="chart-threshold-handle" cx="0" cy=${y} r="4" fill=${color}
                @mousedown=${(e: MouseEvent) => { e.stopPropagation(); this.thresholdDragMoved = false; this.draggingThreshold = t.id; window.addEventListener('keydown', this.onKey, { once: true }); }}
                @click=${(e: MouseEvent) => { e.stopPropagation(); if (!this.thresholdDragMoved) this.dispatchEvent(new CustomEvent('threshold-click', { detail: { id: t.id }, bubbles: true, composed: true })); this.thresholdDragMoved = false; }}></circle>
            </g>`,
          )}
        </g>
      </svg>

      ${measures.map((m) => {
        const centerX = M.left + (m.xA + m.xB) / 2;
        const slopeClass = Math.abs(m.slope) < 0.001 ? 'neutral' : m.slope > 0 ? 'up' : 'down';
        return html`<div class="chart-measure-stats" style=${`bottom:${M.bottom + 8}px;left:${centerX}px;transform:translateX(-50%)`}>
          <div class="measure-stat-row"><span class="measure-label">ΔT</span><span class="measure-value">${formatTimeDelta(m.deltaT)}</span></div>
          <div class="measure-stat-row"><span class="measure-label">ΔV</span><span class="measure-value" style=${`color:${lineColor}`}>${formatStatLabel(m.deltaV)}</span></div>
          <div class="measure-stat-row"><span class="measure-label">Δ/s</span><span class="measure-value measure-slope ${slopeClass}">${formatStatLabel(Math.abs(m.slope))}</span></div>
          <div class="measure-stat-row"><span class="measure-label">min</span><span class="measure-value" style=${`color:${lineColor}`}>${formatStatLabel(m.min)}</span></div>
          <div class="measure-stat-row"><span class="measure-label">max</span><span class="measure-value" style=${`color:${lineColor}`}>${formatStatLabel(m.max)}</span></div>
        </div>`;
      })}

      ${this.config?.showStats
        ? html`<div class="chart-stats-overlay">
            ${(['min', 'max', 'avg', 'rms'] as const).map(
              (k) => html`<div class="chart-stats-row">
                <span class="chart-stats-label">${k}</span><span class="chart-stats-value" data-stat=${k} style=${`color:${lineColor}`}>—</span>
              </div>`,
            )}
          </div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-chart': VitesseChart;
  }
}
